/* eslint-disable react-refresh/only-export-components */
import { preloadRoute } from "@/turboRouteImports";
import { PageProps } from "@/types/page";
import {
    FormDataConvertible,
    FormDataKeys,
    FormDataValues,
    Page,
} from "@inertiajs/core";
import {
    Head as InertiaHead,
    Link as InertiaLink,
    router as inertiaRouter,
    useForm as useInertiaForm,
    usePage as useInertiaPage,
} from "@inertiajs/react";
import axios, { Method } from "axios";
import { ComponentProps, useEffect, useRef, useState } from "react";
import { useParams } from "wouter";
import { navigate } from "wouter/use-browser-location";

export function usePage<TPageProps extends PageProps = PageProps>(): Page<
    Partial<TPageProps>
> {
    if (window.isTurboPage) {
        return {
            props: {},
        } as unknown as Page<Partial<TPageProps>>;
    }
    return useInertiaPage<TPageProps>();
}

export function usePageOrFetch<TPageProps extends PageProps = PageProps>(
    url: string,
): Page<TPageProps> | { props: Partial<TPageProps> } {
    if (window.isTurboPage) {
        const [data, setData] = useState<
            Page<TPageProps> | { props: Partial<TPageProps> }
        >({
            props: {},
        });
        useEffect(() => {
            axios.get(url).then((response) => {
                setData({
                    props: response.data,
                });
            });
        }, []);
        return data;
    }
    return useInertiaPage<TPageProps>();
}

export function useUrlParams() {
    if (window.isTurboPage) {
        return useParams<Record<string, string>>();
    }
    return route().params;
}

/**
 * Mirrors the shape of the Inertia visit option so a single call site can pass
 * one callback to whichever implementation of `useForm` it ends up with.
 */
type TurboSubmitOptions = {
    onSuccess?: (page: { props: unknown }) => void;
};

export function useForm<TForm extends Record<string, FormDataConvertible>>(
    initialValues: TForm,
) {
    if (window.isTurboPage) {
        const [data, changeData] = useState<TForm>(initialValues);
        const [processing, setProcessing] = useState(false);
        const [errors, setErrors] = useState<
            Partial<Record<keyof TForm, string>>
        >({});

        const transformRef = useRef<((data: TForm) => TForm) | null>(null);

        const setData: {
            <K extends FormDataKeys<TForm>>(
                key: K,
                value: FormDataValues<TForm, K>,
            ): void;
            (updater: (previousData: TForm) => TForm): void;
        } = (
            keyOrUpdater: PropertyKey | ((data: TForm) => TForm),
            value?: unknown,
        ) => {
            changeData((prev) => {
                if (typeof keyOrUpdater === "function") {
                    return keyOrUpdater(prev);
                }
                return {
                    ...prev,
                    [keyOrUpdater]: value,
                };
            });
        };

        const reset = () => {
            changeData(initialValues);
            setErrors({});
        };

        const submit = (
            url: string,
            method: Method,
            options?: TurboSubmitOptions,
        ) => {
            setProcessing(true);
            axios
                .request({
                    url,
                    method,
                    data: transformRef.current
                        ? transformRef.current(data)
                        : data,
                })
                .catch((error) => {
                    setErrors(error.response.data.errors);
                    setProcessing(false);
                })
                .then((response) => {
                    if (response?.status !== 200) {
                        return;
                    }

                    options?.onSuccess?.({ props: response.data });

                    // A URL on the Laravel host — a signed auto-login link or a
                    // redirect target — has to be a top-level navigation, since
                    // the point of it is the session cookie Laravel sets there.
                    const appUrl =
                        response.data.loginUrl ?? response.data.redirectUrl;
                    if (typeof appUrl === "string") {
                        window.location.href = appUrl;
                        return;
                    }

                    if (response.data.redirect) {
                        navigate(toTurboUrl(response.data.redirect));
                        return;
                    }
                    setProcessing(false);
                });
        };

        return {
            data,
            setData,
            processing,
            errors,
            get: (url: string, options?: TurboSubmitOptions) =>
                submit(url, "GET", options),
            post: (url: string, options?: TurboSubmitOptions) =>
                submit(url, "POST", options),
            put: (url: string, options?: TurboSubmitOptions) =>
                submit(url, "PUT", options),
            delete: (url: string, options?: TurboSubmitOptions) =>
                submit(url, "DELETE", options),
            transform: (transformer: (data: TForm) => TForm) => {
                transformRef.current = transformer;
            },
            reset,
        };
    }
    return useInertiaForm<TForm>(initialValues);
}

export const router = {
    ...inertiaRouter,
    get: (...args: Parameters<typeof inertiaRouter.get>) => {
        if (window.isTurboPage) {
            navigate(toTurboUrl(args[0].toString()));
            window.scrollTo(0, 0);
        } else {
            return inertiaRouter.get(...args);
        }
    },
    post: (...args: Parameters<typeof inertiaRouter.post>) => {
        if (window.isTurboPage) {
            axios.post(args[0].toString(), args[1]).then((response) => {
                if (response?.status !== 200) {
                    return;
                }
                if (response.data.redirect) {
                    navigate(toTurboUrl(response.data.redirect));
                    window.scrollTo(0, 0);
                }
            });
        } else {
            return inertiaRouter.post(...args);
        }
    },
};

export function Head(props: ComponentProps<typeof InertiaHead>) {
    if (window.isTurboPage) {
        document.title = props.title ?? "";
        return props.children;
    }
    return <InertiaHead {...props} />;
}

export function Link(
    props: ComponentProps<typeof InertiaLink> & { href: string },
) {
    if (window.isTurboPage) {
        return (
            <InertiaLink
                {...props}
                onMouseEnter={(e) => {
                    preloadRoute(props.href);
                    props.onMouseEnter?.(e);
                }}
                onFocus={(e) => {
                    preloadRoute(props.href);
                    props.onFocus?.(e);
                }}
                onTouchStart={(e) => {
                    preloadRoute(props.href);
                    props.onTouchStart?.(e);
                }}
                onClick={(e) => {
                    e.preventDefault();
                    navigate(toTurboUrl(props.href));
                }}
            />
        );
    }
    return <InertiaLink {...props} />;
}

/*
 * Every turbo host and the Laravel host it hands off to.
 *
 * Order is not significant: every app host carries the `app.` label and no
 * `app.`-labelled host is itself a turbo host, so no rule's output can be picked
 * up and rewritten again by a later rule. What keeps the table honest is the
 * anchoring in hostPattern below, not the order of these rows.
 */
const TURBO_HOSTS = [
    { turbo: "coaching-app.test", app: "app.coaching-app.test" },
    { turbo: "turbo.coaching-app.test", app: "app.coaching-app.test" },
    { turbo: "strawberry.me", app: "app.strawberry.me" },
    { turbo: "stagingstrawberry.me", app: "app.stagingstrawberry.me" },
    { turbo: "yaz.stagingstrawberry.me", app: "app.stagingstrawberry.me" },
    { turbo: "testingstrawberry.me", app: "app.testingstrawberry.me" },
    ...thisCheckoutsHosts(),
];

function thisCheckoutsHosts() {
    const appHost = /^https:\/\/(app\.[a-z0-9.-]+)\/?$/.exec(
        import.meta.env.VITE_APP_URL ?? "",
    )?.[1];

    return appHost
        ? [{ turbo: appHost.slice("app.".length), app: appHost }]
        : [];
}

/*
 * Anchored to the start of the string and terminated by a lookahead, so only the
 * URL's own host can match. Four things depend on that: an unanchored needle
 * rewrites "stagingstrawberry.me" via the "strawberry.me" rule (and
 * "testingstrawberry.me" via both); it rewrites a host nested in a query
 * (?next=https://strawberry.me/x); an unterminated match rewrites a lookalike
 * like coaching-app.test.evil.com; and the "stagingstrawberry.me" needle would
 * swallow "yaz.stagingstrawberry.me".
 *
 * A relative href has no scheme, so it falls through untouched — which is
 * correct, since it is already same-host.
 */
function hostPattern(host: string) {
    return new RegExp(`^(https?://)${host.replace(/\./g, "\\.")}(?=[/?#]|$)`);
}

export function fromTurboUrl(url: string) {
    return TURBO_HOSTS.reduce(
        (rewritten, { turbo, app }) =>
            rewritten.replace(hostPattern(turbo), `$1${app}`),
        url,
    );
}

/*
 * The reverse mapping is ambiguous — stagingstrawberry.me and
 * yaz.stagingstrawberry.me both hand off to app.stagingstrawberry.me — so it is
 * resolved against the host this bundle is actually being served from rather
 * than by walking the table. That is also what lets one build serve two hosts:
 * the yaz preview ships the staging bundle verbatim and still links to itself.
 *
 * The host parameter exists so tests don't have to stub window.location.
 */
export function toTurboUrl(url: string, host: string = window.location.host) {
    const pair = TURBO_HOSTS.find((candidate) => candidate.turbo === host);

    if (!pair) {
        return url;
    }

    return url.replace(hostPattern(pair.app), `$1${pair.turbo}`);
}

export function toAppHost(host: string = window.location.host) {
    const pair = TURBO_HOSTS.find((candidate) => candidate.turbo === host);

    return pair?.app ?? host;
}
